"use client"

import { type ReactNode, useCallback, useEffect, useState } from "react";
import CloseIcon from "~/assets/icons/close.svg";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

export default function FullscreenModal({
  open,
  setOpenAction,
  children,
  isDirty,
}: {
  open: boolean;
  setOpenAction: (b: boolean) => void;
  children: ReactNode;
  isDirty?: boolean | null;
}) {
  const [dialog, setDialog] = useState(false);

  const closeModal = useCallback(() => {
    setOpenAction(false);
    setDialog(false);
  }, [setOpenAction, setDialog]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        if (isDirty) setDialog(true);
        else closeModal();
      }
    };

    if (open) {
      document.body.classList.add("modal-active");
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.classList.remove("modal-active");
      document.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.classList.remove("modal-active");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, setDialog, dialog, isDirty, setOpenAction, closeModal]);
  return (
    <>
      {open && (
        <div
          className={
            "modal fixed left-0 top-0 z-50 flex h-screen w-full items-center justify-center bg-white"
          }
        >
          <div className="modal-overlay absolute h-fit w-full bg-white"></div>

          <div className="fixed z-50 h-screen w-full overflow-y-auto pb-40">
            <div
              className="ripple modal-close absolute right-0 top-1 z-50 mr-4 mt-4 flex cursor-pointer flex-col items-center rounded-full p-4 text-sm text-primary transition-all duration-300 ease-linear hover:bg-[#007BB30A] active:bg-[#007BB31F] lg:right-32"
              onClick={() => {
                if (isDirty) {
                  setDialog(true);
                } else {
                  setOpenAction(false);
                }
              }}
            >
              <CloseIcon />
            </div>
            {children}
            {isDirty != null && (
              <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent
                  className={
                    "!rounded-[32px] border-transparent bg-white px-10 py-16 shadow-[0px_4px_16px_0px_#00000014] outline-none"
                  }
                >
                  <DialogHeader>
                    <DialogTitle className="h3 mb-3 text-primary">
                      Potvrdit
                    </DialogTitle>
                    <DialogDescription className="!mb-7 !text-lg">
                      Pokud se teď rozhodnete skončit, o změny přijdete. Opravdu
                      chcete skončit?
                    </DialogDescription>
                    <div className="flex lg:justify-items-end justify-center lg:justify-end gap-3">
                      <DialogClose className="btn btn-secondary ripple outline-none">
                        Zrušit
                      </DialogClose>
                      <button
                        onClick={() => closeModal()}
                        className="btn btn-primary ripple outline-none"
                      >
                        Skončit
                      </button>
                    </div>
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export const FullscreenModalContentWrapper = ({
  children,
  shouldShift,
}: Readonly<{ children: React.ReactNode; shouldShift: boolean }>) => {
  return (
    <>
      <div className="mx-auto flex items-center justify-center align-middle lg:w-8/12 xl:w-4/12">
        <div
          className={
            "modal-content mx-auto h-auto w-full p-4 text-left " +
            (!shouldShift ? "mt-24" : "mt-48")
          }
        >
          {children}
        </div>
      </div>
    </>
  );
};
